/* ==========================================================================
   fk-store-context.js — Flor Keeps store context + fulfilment timing

   Owns one state value: storeContext ∈ { none, austin, dallas } plus a
   `confirmed` flag. Everything geo-conditional on the site reads from here.

   HARD RULES this file exists to enforce (build brief §5, §6, handoff 08):
   - Local mode MESSAGES, it never filters. No catalog is hidden.
   - Fulfilment is never chosen here — checkout resolves it from the address.
   - An unconfirmed guess renders as "Austin?" and never silently claims to
     know where someone is.
   - ALL time maths runs in America/Chicago, never the browser clock.
     (handoff 08 #8 — this is a correctness bug, not a preference.)
   - Everything degrades safely when store data is missing.
   ========================================================================== */

(function () {
  'use strict';

  var STORAGE_KEY = 'fk_store_context';
  var COOKIE_KEY = 'fk_store_context';
  var COOKIE_DAYS = 180;
  var TZ = 'America/Chicago';

  /* Pickup ready time, in minutes. Operator-confirmed: 1 hour.
     (Resolves handoff 08 #1 — it was previously stated three different ways.) */
  var PICKUP_READY_MINUTES = 60;

  /* Cutoff rule. Operator decision: delivery cutoff is the SAME as pickup.
     Both derive as max(open, close - 2h) until a real per-day table lands in
     the store settings. This also removes the Sunday-11AM breakage the old
     close-4h delivery rule produced. See IMPLEMENT-LATER D1. */
  var CUTOFF_HOURS_BEFORE_CLOSE = 2;

  var VALID = ['none', 'austin', 'dallas'];

  /* ---------------------------------------------------------------------
     Time — every function below is pinned to America/Chicago.
     --------------------------------------------------------------------- */

  var partsFormatter = null;
  function getPartsFormatter() {
    if (partsFormatter) return partsFormatter;
    try {
      partsFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: TZ,
        hour12: false,
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      /* Environment without full ICU — fall back to the browser clock rather
         than throwing. Timing copy may be off; availability still renders. */
      partsFormatter = null;
    }
    return partsFormatter;
  }

  var WEEKDAY_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  /**
   * Current wall-clock time at the store, as plain parts.
   * @returns {{year:number, month:number, day:number, hour:number, minute:number, dow:number, minutes:number}}
   */
  function storeNow(date) {
    var d = date || new Date();
    var fmt = getPartsFormatter();

    if (!fmt) {
      return {
        year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(),
        hour: d.getHours(), minute: d.getMinutes(), dow: d.getDay(),
        minutes: d.getHours() * 60 + d.getMinutes()
      };
    }

    var out = {};
    fmt.formatToParts(d).forEach(function (p) {
      if (p.type === 'weekday') out.dow = WEEKDAY_INDEX[p.value];
      else if (p.type !== 'literal') out[p.type] = parseInt(p.value, 10);
    });

    /* Intl renders midnight as hour 24 in some engines. Normalise. */
    if (out.hour === 24) out.hour = 0;

    return {
      year: out.year, month: out.month, day: out.day,
      hour: out.hour, minute: out.minute,
      dow: typeof out.dow === 'number' ? out.dow : new Date().getDay(),
      minutes: out.hour * 60 + out.minute
    };
  }

  /** ISO date string (YYYY-MM-DD) for a store-local day offset from today. */
  function storeDateISO(offsetDays) {
    var now = storeNow();
    var d = new Date(Date.UTC(now.year, now.month - 1, now.day));
    d.setUTCDate(d.getUTCDate() + (offsetDays || 0));
    return d.toISOString().slice(0, 10);
  }

  var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  function formatHour(hour24) {
    if (hour24 == null) return '';
    var h = hour24 % 24;
    var suffix = h >= 12 ? 'PM' : 'AM';
    var display = h % 12;
    if (display === 0) display = 12;
    return display + ' ' + suffix;
  }

  function formatDuration(totalMinutes) {
    if (totalMinutes == null || totalMinutes <= 0) return '';
    var h = Math.floor(totalMinutes / 60);
    var m = totalMinutes % 60;
    if (h && m) return h + 'h ' + m + 'm';
    if (h) return h + 'h';
    return m + 'm';
  }

  /* ---------------------------------------------------------------------
     Store data — supplied by snippets/fk-store-data.liquid as window.FKStores.
     Every read below tolerates the whole thing being absent.
     --------------------------------------------------------------------- */

  function allStores() {
    return (window.FKStores && window.FKStores.stores) || [];
  }

  function getStore(key) {
    if (!key || key === 'none') return null;
    var found = null;
    allStores().forEach(function (s) {
      if (s && s.key === key) found = s;
    });
    return found;
  }

  /**
   * Is the store closed on a given store-local date (holiday / ad-hoc)?
   * Reads an empty list safely — closures are still pending from the operator
   * (IMPLEMENT-LATER D5), so today this always returns false.
   */
  function isClosedDate(store, isoDate) {
    if (!store || !store.closures || !store.closures.length) return false;
    return store.closures.indexOf(isoDate) !== -1;
  }

  /** [openHour, closeHour] for a store on a given day-of-week, or null. */
  function hoursFor(store, dow) {
    if (!store || !store.week || !store.week.length) return null;
    var pair = store.week[dow];
    if (!pair || pair.length !== 2) return null;
    if (pair[0] == null || pair[1] == null) return null;
    return { open: pair[0], close: pair[1] };
  }

  /** Cutoff hour for a store on a given day. Same value for pickup + delivery. */
  function cutoffHourFor(store, dow) {
    var hrs = hoursFor(store, dow);
    if (!hrs) return null;

    /* A real per-day table, once supplied, wins over the derived rule. */
    if (store.cutoffs && store.cutoffs.length && store.cutoffs[dow] != null) {
      return store.cutoffs[dow];
    }
    return Math.max(hrs.open, hrs.close - CUTOFF_HOURS_BEFORE_CLOSE);
  }

  /** The next day index (0-6) the store is actually open, searching forward. */
  function nextOpenDay(store, fromDow) {
    for (var i = 1; i <= 7; i++) {
      var dow = (fromDow + i) % 7;
      if (hoursFor(store, dow) && !isClosedDate(store, storeDateISO(i))) {
        return { dow: dow, offset: i };
      }
    }
    return null;
  }

  /**
   * Full timing picture for a store right now.
   * Shape is stable regardless of missing data — callers never need guards.
   */
  function status(key) {
    var store = getStore(key);
    var now = storeNow();

    var base = {
      key: key || 'none',
      store: store,
      known: !!store,
      state: 'unknown',      /* open | before-open | after-close | closed | unknown */
      openHour: null,
      closeHour: null,
      cutoffHour: null,
      minutesToCutoff: null,
      countdown: '',
      urgent: false,          /* under 90 minutes to cutoff */
      pastCutoff: false,
      readyMinutes: PICKUP_READY_MINUTES,
      nextOpenDayName: '',
      nextOpenHour: null
    };

    if (!store) return base;

    var closedToday = isClosedDate(store, storeDateISO(0));
    var hrs = closedToday ? null : hoursFor(store, now.dow);

    var next = nextOpenDay(store, now.dow);
    if (next) {
      var nextHrs = hoursFor(store, next.dow);
      base.nextOpenDayName = next.offset === 1 ? 'tomorrow' : DAY_NAMES[next.dow];
      base.nextOpenHour = nextHrs ? nextHrs.open : null;
    }

    if (!hrs) {
      base.state = 'closed';
      base.pastCutoff = true;
      return base;
    }

    base.openHour = hrs.open;
    base.closeHour = hrs.close;
    base.cutoffHour = cutoffHourFor(store, now.dow);

    var cutoffMinutes = base.cutoffHour * 60;
    base.minutesToCutoff = cutoffMinutes - now.minutes;

    if (now.minutes < hrs.open * 60) {
      base.state = 'before-open';
    } else if (now.minutes >= hrs.close * 60) {
      base.state = 'after-close';
    } else {
      base.state = 'open';
    }

    if (base.minutesToCutoff <= 0) {
      base.pastCutoff = true;
      base.minutesToCutoff = 0;
    } else {
      base.countdown = formatDuration(base.minutesToCutoff);
      base.urgent = base.minutesToCutoff < 90;
    }

    return base;
  }

  /* ---------------------------------------------------------------------
     Zip resolution — messaging only. Never a gate.
     An out-of-range zip downgrades to a buyable path (shipping), never a
     rejection. (Build brief §5.)
     --------------------------------------------------------------------- */

  function zoneForZip(zip) {
    var clean = String(zip || '').replace(/\D/g, '').slice(0, 5);
    if (clean.length < 3) return null;
    var prefix = clean.slice(0, 3);
    var result = { zone: 'national', store: null };

    /* A configured entry matches as a full 5-digit zip or a 3-digit prefix,
       so the lists can mix both. The same-day lists carry the measured 40-mile
       zip sets (exact); prefixes still work for coarser areas.

       RETIRED 2026-08-28: the 'pickup' zone and its `zipDrive` list. It named
       a ring that was drivable-but-not-deliverable, which stopped being a real
       distinction once the same-day lists were measured properly — every zip
       we serve is now in zipIn, and anyone in it can both collect and have it
       delivered. Two zones remain: 'delivery' and 'national'. */
    function inList(list) {
      if (!list || !list.length) return false;
      return list.indexOf(clean) !== -1 || list.indexOf(prefix) !== -1;
    }

    allStores().forEach(function (s) {
      if (result.zone === 'delivery') return;
      if (inList(s.zipIn)) {
        result = { zone: 'delivery', store: s.key };
      }
    });

    return result;
  }

  /* ---------------------------------------------------------------------
     Persistence
     --------------------------------------------------------------------- */

  function readCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function writeCookie(name, value) {
    var expires = new Date();
    expires.setTime(expires.getTime() + COOKIE_DAYS * 864e5);
    document.cookie = name + '=' + encodeURIComponent(value) +
      ';expires=' + expires.toUTCString() + ';path=/;SameSite=Lax';
  }

  function load() {
    var raw = null;
    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch (e) { /* private mode — cookie is the fallback */ }

    if (!raw) raw = readCookie(COOKIE_KEY);
    if (!raw) return { value: 'none', confirmed: false };

    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return { value: 'none', confirmed: false };
    }

    if (!parsed || VALID.indexOf(parsed.value) === -1) {
      return { value: 'none', confirmed: false };
    }
    return { value: parsed.value, confirmed: !!parsed.confirmed };
  }

  function save(state) {
    var raw = JSON.stringify(state);
    try {
      window.localStorage.setItem(STORAGE_KEY, raw);
    } catch (e) { /* fall through to cookie */ }
    writeCookie(COOKIE_KEY, raw);
  }

  /* ---------------------------------------------------------------------
     Public API
     --------------------------------------------------------------------- */

  var state = load();

  var FK = window.FK || (window.FK = {});

  var StoreContext = {
    TZ: TZ,
    PICKUP_READY_MINUTES: PICKUP_READY_MINUTES,

    /** 'none' | 'austin' | 'dallas' */
    get: function () { return state.value; },

    /** True only when the shopper explicitly chose. A geo guess is false. */
    isConfirmed: function () { return state.confirmed; },

    isLocal: function () { return state.value !== 'none'; },

    /** The store record, or null when national/unknown. */
    store: function () { return getStore(state.value); },

    stores: allStores,
    getStore: getStore,

    /**
     * The next day this store opens, skipping holiday closures.
     * Exported because sections were recomputing it WITHOUT the closure check,
     * which pairs the right day name with the wrong day's hours the moment the
     * operator enters closures.
     * @returns {{dow:number, offset:number}|null}
     */
    nextOpenDay: nextOpenDay,

    /**
     * Set the context.
     * @param {string} value 'none' | 'austin' | 'dallas'
     * @param {boolean} confirmed false for an unconfirmed geo guess
     */
    set: function (value, confirmed) {
      if (VALID.indexOf(value) === -1) return;
      state = { value: value, confirmed: confirmed !== false };
      save(state);
      this.apply();
      document.dispatchEvent(new CustomEvent('fk:store-context-changed', {
        detail: { value: state.value, confirmed: state.confirmed }
      }));
    },

    /** Suggest a store from geo without locking it in — renders as "Austin?" */
    suggest: function (value) {
      if (state.confirmed) return;            /* never override an explicit choice */
      this.set(value, false);
    },

    status: function (key) { return status(key || state.value); },
    zoneForZip: zoneForZip,

    /* Time helpers, exposed so sections never reimplement them */
    now: storeNow,
    formatHour: formatHour,
    formatDuration: formatDuration,
    dayName: function (dow) { return DAY_NAMES[dow]; },

    /**
     * Reflect the current context onto the document so CSS and Liquid-rendered
     * markup can respond without a re-fetch. Sections listen for the event.
     */
    apply: function () {
      var root = document.documentElement;
      root.setAttribute('data-fk-context', state.value);
      root.setAttribute('data-fk-context-confirmed', state.confirmed ? 'true' : 'false');
      root.setAttribute('data-fk-local', state.value === 'none' ? 'false' : 'true');
    }
  };

  FK.StoreContext = StoreContext;
  StoreContext.apply();

  /* Re-apply after Shopify section re-renders in the theme editor. */
  document.addEventListener('shopify:section:load', function () {
    StoreContext.apply();
  });

  /* One tick a minute drives every countdown on the page — not once a second.
     (04-SHOPIFY-BUILD § Performance notes.) */
  window.setInterval(function () {
    document.dispatchEvent(new CustomEvent('fk:tick'));
  }, 60000);
})();
